(() => {
  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const cartUrl = (path) => `${routesRoot()}${String(path).replace(/^\//, '')}`;

  const normaliseProperties = (properties = {}) => {
    const entries = Array.isArray(properties)
      ? properties.filter((entry) => Array.isArray(entry) && entry.length >= 2)
      : Object.entries(properties || {});

    return Object.fromEntries(
      entries
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => [String(key), String(value)])
        .sort(([left], [right]) => left.localeCompare(right))
    );
  };

  const propertySignature = (properties = {}) => JSON.stringify(
    normaliseProperties(properties)
  );

  const sameProperties = (left, right) => (
    propertySignature(left) === propertySignature(right)
  );

  const responseError = (payload, fallback) => (
    payload?.description ||
    payload?.message ||
    payload?.errors ||
    fallback
  );

  const fetchJson = async (
    url,
    options = {},
    fallbackMessage = 'The bag could not be updated.'
  ) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok || payload?.status) {
      throw new Error(responseError(payload, fallbackMessage));
    }

    return payload;
  };

  const fetchCart = () => fetchJson(
    cartUrl('cart.js'),
    {},
    'The current bag could not be confirmed.'
  );

  const matchingLines = (cart, variantId, properties) => (
    (cart?.items || []).filter((line) => (
      Number(line.variant_id || line.id || 0) === Number(variantId || 0) &&
      sameProperties(line.properties, properties)
    ))
  );

  const originalLine = (cart, config) => {
    const byKey = (cart?.items || []).find((line) => (
      String(line.key || '') === String(config.key || '')
    ));

    if (byKey) return byKey;

    return (cart?.items || []).find((line) => (
      Number(line.variant_id || line.id || 0) === Number(config.originalVariantId || 0) &&
      Number(line.product_id || 0) === Number(config.originalProductId || 0) &&
      sameProperties(line.properties, config.properties)
    )) || null;
  };

  const updateLines = (updates, includeSections = true) => fetchJson(
    cartUrl('cart/update.js'),
    {
      method: 'POST',
      body: JSON.stringify({
        updates,
        ...(includeSections ? { sections: 'push-cart,cart-count' } : {})
      })
    },
    'The bag could not be restored.'
  );

  document.addEventListener('alpine:init', () => {
    Alpine.store('cartEditor', {
      active: false,
      loading: false,
      saving: false,
      error: '',
      notice: '',
      target: null,
      requestController: null,
      returnFocusElement: null,

      key: '',
      properties: {},
      originalProductId: 0,
      originalProductHandle: '',
      originalVariantId: 0,
      originalVariantTitle: '',
      productLimit: 0,
      originalQuantity: 1,

      product: null,
      selectedVariantId: 0,
      quantity: 1,
      pendingSizeTitle: '',
      requestedHandle: '',

      get selectedVariant() {
        return this.product?.variants?.find((variant) => (
          Number(variant.id) === Number(this.selectedVariantId)
        )) || null;
      },

      get selectedSizeTitle() {
        return this.selectedVariant?.title || this.pendingSizeTitle || this.originalVariantTitle;
      },

      get variantChanged() {
        return Boolean(
          this.selectedVariantId &&
          Number(this.selectedVariantId) !== Number(this.originalVariantId)
        );
      },

      get quantityChanged() {
        return Number(this.quantity) !== Number(this.originalQuantity);
      },

      get hasChanges() {
        return this.variantChanged || this.quantityChanged;
      },

      get canSave() {
        return Boolean(
          !this.loading &&
          !this.saving &&
          this.hasChanges &&
          this.selectedVariant &&
          this.isVariantSelectable(this.selectedVariant)
        );
      },

      configure(config = {}) {
        this.key = String(config.key || '');
        this.properties = normaliseProperties(config.properties);
        this.originalProductId = Number(config.productId || config.originalProductId || 0);
        this.originalProductHandle = String(config.productHandle || config.originalProductHandle || '');
        this.originalVariantId = Number(config.variantId || config.originalVariantId || 0);
        this.originalVariantTitle = String(config.variantTitle || config.originalVariantTitle || '');
        this.productLimit = Math.max(0, Number(config.productLimit || 0));
        this.originalQuantity = Math.max(1, Number(config.quantity || 1));
        this.quantity = this.originalQuantity;
        this.selectedVariantId = this.originalVariantId;
        this.pendingSizeTitle = this.originalVariantTitle;
        this.product = null;
        this.error = '';
        this.notice = '';
      },

      async openFromConfig(configId, target, trigger = null) {
        const configElement = document.getElementById(String(configId || ''));

        if (!configElement) {
          this.error = 'The product options could not be opened. Close the bag and try again.';
          return;
        }

        try {
          this.configure(JSON.parse(configElement.textContent || '{}'));
        } catch (error) {
          console.error('Cart editor configuration could not be parsed:', error);
          this.error = 'The product options could not be opened. Close the bag and try again.';
          return;
        }

        this.target = target instanceof Element ? target : null;
        this.returnFocusElement = trigger instanceof HTMLElement
          ? trigger
          : document.activeElement;
        this.active = true;

        const cartStore = Alpine.store('cart');
        if (cartStore) cartStore.mode = 'edit';

        await this.loadProduct(
          this.originalProductHandle,
          this.originalVariantTitle,
          this.originalVariantId
        );
      },

      async loadProduct(handle, preferredSizeTitle = '', preferredVariantId = 0) {
        if (!handle || !this.target) return;

        this.requestController?.abort();
        this.requestController = new AbortController();
        this.loading = true;
        this.error = '';
        this.notice = 'Loading product options…';
        this.pendingSizeTitle = preferredSizeTitle || this.selectedSizeTitle || '';
        this.requestedHandle = String(handle);

        const url = new URL(
          `${routesRoot()}products/${encodeURIComponent(handle)}`,
          window.location.origin
        );

        url.searchParams.set('view', 'cart-line-editor');

        if (preferredVariantId) {
          url.searchParams.set('variant', String(preferredVariantId));
        }

        try {
          await window.FAFSections.render({
            url: url.toString(),
            target: this.target,
            selector: '#shopify-section-cart-line-editor',
            signal: this.requestController.signal,
            context: { sectionId: 'cart-line-editor' }
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.error = error.message || 'Product options could not be loaded.';
            this.notice = '';
          }
        } finally {
          this.loading = false;
          this.requestController = null;
        }
      },

      setProduct(config = {}) {
        const variants = (Array.isArray(config.variants) ? config.variants : [])
          .map((variant) => ({
            ...variant,
            id: Number(variant.id || 0),
            productId: Number(variant.productId || config.id || 0),
            inventoryQuantity: Math.max(0, Number(variant.inventoryQuantity || 0)),
            available: variant.available === true,
            tracksInventory: variant.tracksInventory === true
          }))
          .filter((variant) => variant.id);

        this.product = {
          ...config,
          id: Number(config.id || 0),
          productLimit: Math.max(0, Number(config.productLimit || 0)),
          variants
        };

        const cartStore = Alpine.store('cart');

        cartStore?.registerProductRule?.(
          this.product.id,
          this.product.productLimit,
          Object.fromEntries(variants.map((variant) => [variant.id, {
            productId: this.product.id,
            tracksInventory: variant.tracksInventory,
            inventoryPolicy: variant.inventoryPolicy,
            inventoryQuantity: variant.inventoryQuantity
          }]))
        );

        const matchingSize = variants.find((variant) => (
          String(variant.title) === String(this.pendingSizeTitle) &&
          this.isVariantSelectable(variant)
        ));

        const originalSelection = variants.find((variant) => (
          Number(variant.id) === Number(this.originalVariantId) &&
          this.isVariantSelectable(variant)
        ));

        const selected = matchingSize || originalSelection || variants.find((variant) => (
          this.isVariantSelectable(variant)
        ));

        this.selectedVariantId = Number(selected?.id || 0);

        if (!selected) {
          this.notice = 'No selectable sizes are currently available in this colour.';
        } else if (
          this.pendingSizeTitle &&
          String(selected.title) !== String(this.pendingSizeTitle)
        ) {
          this.notice = `${this.displaySize(this.pendingSizeTitle)} is unavailable in this colour. Please choose another size.`;
        } else {
          this.notice = '';
        }

        this.error = '';
      },

      selectVariant(variantId) {
        const variant = this.product?.variants?.find((entry) => (
          Number(entry.id) === Number(variantId)
        ));

        if (!this.isVariantSelectable(variant)) return;

        this.selectedVariantId = Number(variant.id);
        this.pendingSizeTitle = String(variant.title || '');
        this.error = '';
        this.notice = '';
      },

      async selectLinkedProduct(handle) {
        if (!handle || this.loading || this.saving) return;
        await this.loadProduct(handle, this.selectedSizeTitle);
      },

      displaySize(title, product = null) {
        const currentProduct = product || this.product || {};

        return window.FAFSizes?.format?.(String(title || ''), {
          gender: currentProduct.gender || '',
          category: currentProduct.category || ''
        }) || String(title || '');
      },

      projectedVariantQuantity(variant) {
        const cartStore = Alpine.store('cart');
        const inCart = Number(cartStore?.quantityFor?.(variant.id) || 0);
        const originalOffset = Number(variant.id) === Number(this.originalVariantId)
          ? this.originalQuantity
          : 0;

        return Math.max(0, inCart - originalOffset + Number(this.quantity || 0));
      },

      projectedProductQuantity(productId) {
        const cartStore = Alpine.store('cart');
        const inCart = Number(cartStore?.quantityForProduct?.(productId) || 0);
        const originalOffset = Number(productId) === Number(this.originalProductId)
          ? this.originalQuantity
          : 0;

        return Math.max(0, inCart - originalOffset + Number(this.quantity || 0));
      },

      isVariantSelectable(variant) {
        if (!variant || !variant.available) return false;

        if (
          variant.tracksInventory &&
          variant.inventoryPolicy !== 'continue' &&
          this.projectedVariantQuantity(variant) > variant.inventoryQuantity
        ) {
          return false;
        }

        const productLimit = Math.max(
          0,
          Number(this.product?.productLimit || this.productLimit || 0)
        );

        if (
          productLimit > 0 &&
          this.projectedProductQuantity(variant.productId) > productLimit
        ) {
          return false;
        }

        return true;
      },

      availabilityLabel(variant) {
        if (!variant?.available) return 'Unavailable';
        if (!this.isVariantSelectable(variant)) {
          return 'Not enough stock for the selected quantity';
        }
        return 'Available';
      },

      setQuantity(value) {
        const quantity = Math.max(1, Number(value || 1));
        const previous = this.quantity;
        this.quantity = quantity;

        if (this.selectedVariant && !this.isVariantSelectable(this.selectedVariant)) {
          this.quantity = previous;
          this.error = 'The selected quantity is not available.';
          return;
        }

        this.error = '';
      },

      increaseQuantity() {
        this.setQuantity(Number(this.quantity) + 1);
      },

      decreaseQuantity() {
        this.setQuantity(Math.max(1, Number(this.quantity) - 1));
      },

      async addReplacement(variantId) {
        return fetchJson(
          cartUrl('cart/add.js'),
          {
            method: 'POST',
            body: JSON.stringify({
              items: [{
                id: Number(variantId),
                quantity: Number(this.quantity),
                properties: normaliseProperties(this.properties)
              }]
            })
          },
          'The selected colour or size could not be added.'
        );
      },

      async removeOriginal(lineKey) {
        return fetchJson(
          cartUrl('cart/change.js'),
          {
            method: 'POST',
            body: JSON.stringify({
              id: lineKey,
              quantity: 0,
              sections: 'push-cart,cart-count'
            })
          },
          'The original item could not be replaced.'
        );
      },

      async changeQuantity(lineKey) {
        return fetchJson(
          cartUrl('cart/change.js'),
          {
            method: 'POST',
            body: JSON.stringify({
              id: lineKey,
              quantity: Number(this.quantity),
              sections: 'push-cart,cart-count'
            })
          },
          'The quantity could not be updated.'
        );
      },

      async rollbackReplacement(targetVariantId, baselineQuantity) {
        const currentCart = await fetchCart();
        const lines = matchingLines(currentCart, targetVariantId, this.properties);

        if (!lines.length) return currentCart;

        const updates = {};

        lines.forEach((line, index) => {
          updates[line.key] = index === 0 ? baselineQuantity : 0;
        });

        const restoredCart = await updateLines(updates, true);
        Alpine.store('cart').mode = 'cart';

        window.FAFSections.applyCartSections(restoredCart, { openPanel: true });
        return restoredCart;
      },

      async save() {
        if (!this.canSave) return;

        this.saving = true;
        this.error = '';
        this.notice = 'Updating your bag…';

        const targetVariantId = Number(this.selectedVariantId);
        let replacementAdded = false;
        let baselineTargetQuantity = 0;

        try {
          const baselineCart = await fetchCart();
          const baselineOriginal = originalLine(baselineCart, this);

          if (!baselineOriginal) {
            throw new Error('This item has changed in your bag. Return to the bag and try again.');
          }

          let finalCart;

          if (!this.variantChanged) {
            finalCart = await this.changeQuantity(baselineOriginal.key);
          } else {
            baselineTargetQuantity = matchingLines(
              baselineCart,
              targetVariantId,
              this.properties
            ).reduce((total, line) => total + Number(line.quantity || 0), 0);

            await this.addReplacement(targetVariantId);
            replacementAdded = true;

            const cartAfterAdd = await fetchCart();
            const refreshedOriginal = originalLine(cartAfterAdd, this);

            if (!refreshedOriginal) {
              throw new Error('The original item could not be identified after the update.');
            }

            finalCart = await this.removeOriginal(refreshedOriginal.key);
          }

          Alpine.store('cart').mode =
            'cart';

          if (
            window.FAFThemeConfig
              ?.elevarManualCartEvents
          ) {
            const finalLine =
              matchingLines(
                finalCart,
                targetVariantId,
                this.properties
              )[0] || null;

            const originalQuantity =
              Number(
                baselineOriginal.quantity ||
                0
              );

            const finalQuantity =
              Number(
                finalLine?.quantity ??
                this.quantity ??
                0
              );

            if (this.variantChanged) {
              window.FAFAnalytics
                ?.removeFromCart(
                  baselineOriginal,
                  {
                    quantity:
                      originalQuantity,
                    source:
                      'cart_editor'
                  }
                );

              if (finalLine) {
                window.FAFAnalytics
                  ?.addToCart(
                    finalLine,
                    {
                      quantity:
                        finalQuantity,
                      source:
                        'cart_editor'
                    }
                  );
              }
            } else {
              const quantityDelta =
                finalQuantity -
                originalQuantity;

              if (quantityDelta > 0) {
                window.FAFAnalytics
                  ?.addToCart(
                    finalLine ||
                    baselineOriginal,
                    {
                      quantity:
                        quantityDelta,
                      source:
                        'cart_editor'
                    }
                  );
              }

              if (quantityDelta < 0) {
                window.FAFAnalytics
                  ?.removeFromCart(
                    baselineOriginal,
                    {
                      quantity:
                        Math.abs(
                          quantityDelta
                        ),
                      source:
                        'cart_editor'
                    }
                  );
              }
            }

            window.FAFAnalytics
              ?.customizeItem(
                finalLine ||
                baselineOriginal,
                {
                  fromAjax: true,
                  source:
                    'cart_editor'
                }
              );
          }

          window.FAFSections
            .applyCartSections(
              finalCart,
              {
                openPanel: true
              }
            );

          window.dispatchEvent(new CustomEvent('cart-line:edited', {
            detail: {
              fromVariantId: this.originalVariantId,
              toVariantId: targetVariantId,
              quantity: Number(this.quantity)
            }
          }));

          this.reset(false);
        } catch (failure) {
          let rollbackFailed = false;

          if (replacementAdded) {
            try {
              await this.rollbackReplacement(targetVariantId, baselineTargetQuantity);
            } catch (rollbackError) {
              rollbackFailed = true;
              console.error('Cart editor rollback failed:', rollbackError);
            }
          }

          this.error = rollbackFailed
            ? 'The change and automatic rollback both failed. Please review your bag before checkout.'
            : (failure?.message || 'The item could not be updated. Please try again.');
          this.notice = '';
        } finally {
          this.saving = false;
        }
      },

      cancel(restoreFocus = true) {
        if (this.saving) return;

        this.requestController?.abort();
        Alpine.store('cart').mode = 'cart';
        this.reset(restoreFocus);
      },

      closePanel() {
        this.requestController?.abort();
        Alpine.store('cart').mode = 'cart';
        Alpine.store('lazyPanel')?.closePanel?.();
        this.reset(false);
      },

      retry() {
        return this.loadProduct(
          this.requestedHandle || this.product?.handle || this.originalProductHandle,
          this.selectedSizeTitle,
          this.selectedVariantId || this.originalVariantId
        );
      },

      reset(restoreFocus = true) {
        const focusTarget = this.returnFocusElement;

        this.active = false;
        this.loading = false;
        this.error = '';
        this.notice = '';
        this.target = null;
        this.product = null;
        this.selectedVariantId = 0;
        this.requestedHandle = '';
        this.requestController = null;
        this.returnFocusElement = null;

        if (restoreFocus && focusTarget?.isConnected) {
          window.requestAnimationFrame(() => {
            focusTarget.focus({ preventScroll: true });
          });
        }
      }
    });

    Alpine.data('cartEditorShell', () => ({
      openFromEvent(event) {
        const detail = event?.detail || {};

        this.$store.cartEditor.openFromConfig(
          detail.configId,
          this.$refs.content,
          detail.trigger || document.activeElement
        ).then(() => {
          this.$nextTick(() => {
            window.requestAnimationFrame(() => {
              this.$refs.heading?.focus({ preventScroll: true });
            });
          });
        });
      },

      cancel() {
        this.$store.cartEditor.cancel();
      },

      closePanel() {
        this.$store.cartEditor.closePanel();
      }
    }));

    Alpine.data('cartEditorProduct', (config = {}) => ({
      config,

      init() {
        this.$store.cartEditor.setProduct(config);
      },

      selectVariant(variantId) {
        this.$store.cartEditor.selectVariant(variantId);
      },

      selectLinkedProduct(handle) {
        this.$store.cartEditor.selectLinkedProduct(handle);
      },

      variant(variantId) {
        return (config.variants || []).find((entry) => (
          Number(entry.id) === Number(variantId)
        )) || null;
      },

      isVariantSelectable(variantId) {
        const liveVariant = this.$store.cartEditor.product?.variants?.find((entry) => (
          Number(entry.id) === Number(variantId)
        ));

        return this.$store.cartEditor.isVariantSelectable(liveVariant || this.variant(variantId));
      },

      availabilityLabel(variantId) {
        const liveVariant = this.$store.cartEditor.product?.variants?.find((entry) => (
          Number(entry.id) === Number(variantId)
        ));

        return this.$store.cartEditor.availabilityLabel(liveVariant || this.variant(variantId));
      },

      displaySize(title) {
        return this.$store.cartEditor.displaySize(title, config);
      }
    }));
  });

  window.addEventListener('lazy-panel:closed', () => {
    const editor = window.Alpine?.store?.('cartEditor');
    if (editor?.active) editor.reset(false);

    const cart = window.Alpine?.store?.('cart');
    if (cart?.mode === 'edit') cart.mode = 'cart';
  });
})();